import { createForm, useCreateFormStore } from '@components/form';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { hourOptions, minuteOptions } from '@utils/hoursOptions';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  ActionTimeSelector,
  EditableActionContent,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  TravellerActionHeader,
  useEditingContext,
  WaitForDateStateGroup,
} from '../common';
import { ActionTimeWithTimezoneTooltip, newDateIgnoreLocalTimezone } from '../timezone';

const ACTION_TYPE = ACTION_TYPES.wait_until_fixed_date;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE}>
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = { action: TravellerJourneyActionDetailed<typeof ACTION_TYPE> };

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();

  const [offset] = action?.timezone?.split(' ') as string[];
  const selectedDate = newDateIgnoreLocalTimezone(action?.scheduledDate as string);
  const hour = selectedDate.getHours()?.toString();
  const minute = selectedDate.getMinutes()?.toString();

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        dateSuccess={action.result?.dateSucceeded}
        isCard={false}
        progressPercentage=" "
      />

      <Stack space={2}>
        <div>
          <Text font="semibold14" text={t('configuration')} />
          <Text font="regular14" text={t('configurationDescription')} />
          <Text font="regular14" color="elementBlue100" text={t('selectDate')} />
        </div>
        <div>
          <Text font="semibold14" text={t('triggersNextAction')} />
          <ActionTimeWithTimezoneTooltip
            actionText={selectedDate ? abbreviateDate(selectedDate, 'en', 'datetime') : ''}
            hour={hour}
            minute={minute}
            journeyTimezoneOffset={offset}
          />
        </div>
      </Stack>
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type WaitUntilFixedData = {
  date: Date;
  hour: string;
  minute: string;
};

const Form = createForm<WaitUntilFixedData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const [offset] = action?.timezone?.split(' ') ?? [''];
  const hour = formStore.useFieldValue('hour') as string;
  const minute = formStore.useFieldValue('minute') as string;
  const values = action.values;
  const date = values?.date ? newDateIgnoreLocalTimezone(values.date.value) : '';
  const dateString = date
    ? t('selectDateInfo', {
        date: abbreviateDate(date, 'en'),
        hour: hourOptions.find((option) => option.value === hour)?.label ?? '',
        minute: minuteOptions.find((option) => option.value === minute)?.label ?? '',
      })
    : t('noDateSelected');

  const handleSave = (data: WaitUntilFixedData) => {
    const date = new Date(
      Date.UTC(
        data.date.getFullYear(),
        data.date.getMonth(),
        data.date.getDate(),
        Number(data.hour),
        Number(data.minute)
      )
    );
    return editMutation.mutateAsync({
      values: {
        date: { value: date.toISOString() },
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <Stack space={4}>
          <WaitForDateStateGroup selectedStateId={action.action} />
          <Text font="regular14" text={t('triggersOnSelected')} />
        </Stack>
        <Separator />
        <Stack space={4}>
          <Form.DatePicker
            defaultValue={date}
            id="date"
            label={t('date')}
            placeholder="DD / MM / YYYY"
            validators={['required']}
          />
          <ActionTimeSelector
            defaultHour={date ? date.getHours().toString() : ''}
            defaultMinute={date ? date.getMinutes().toString() : ''}
          />
          <ActionTimeWithTimezoneTooltip
            actionText={dateString}
            hour={hour}
            minute={minute}
            journeyTimezoneOffset={offset}
          />
        </Stack>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    completed: 'Completed',
    error: 'Error',
    notStarted: 'Not started',
    inProgress: 'In progress',
    configuration: 'Configuration',
    selectDate: 'Select date',
    configurationDescription: 'The following action/s will be triggered at',
    triggersNextAction: 'Triggers next action',
    daysPassed: '{days} Days',
    triggersOnSelected: 'Triggers the following action on select date',
    date: 'Date',
    hour: 'Hour',
    minute: 'Minute',
    selectDateInfo: 'Triggers the following action on {date} at {hour}:{minute}',
    noDateSelected: 'No date selected',
  },
};
